import { TenantConfiguration } from '@policy-tracker/types';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage, sessionStorage } from 'aws-amplify/utils';
import { getCognitoHostedDomain } from './lib/cognito-domain';

/** Prefix Cognito uses for every persisted token key. */
const COGNITO_KEY_PREFIX = 'CognitoIdentityServiceProvider.';

/**
 * Returns true when the supplied Web Storage object currently holds at least
 * one Cognito-managed key. Wrapped in try/catch because `window.localStorage`
 * / `window.sessionStorage` access can throw in privacy modes / disabled
 * storage; callers treat a thrown probe as "no tokens here".
 */
function storageHasCognitoKeys(storage: Storage): boolean {
    try {
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key !== null && key.startsWith(COGNITO_KEY_PREFIX)) {
                return true;
            }
        }
        return false;
    } catch {
        return false;
    }
}

/**
 * Picks whichever storage backend currently holds Cognito tokens, so that a
 * freshly-reloaded page restores the session from wherever the user's
 * previous sign-in landed. localStorage wins ties (most common case + survives
 * browser restart). On a fresh visit with no Cognito tokens anywhere we
 * default to localStorage; `setLoginStorage()` re-points the provider
 * appropriately at sign-in time. (Regression #1985 — was unconditionally
 * resetting to `defaultStorage` on every page load, which masked sessionStorage
 * tokens written by the "Stay signed in" feature in #1944.)
 */
function pickTokenStorage(): typeof defaultStorage {
    if (storageHasCognitoKeys(window.localStorage)) {
        return defaultStorage;
    }
    if (storageHasCognitoKeys(window.sessionStorage)) {
        return sessionStorage;
    }
    return defaultStorage;
}

export class TenantNotFoundError extends Error {
    constructor() {
        super('Tenant not found');
        this.name = 'TenantNotFoundError';
    }
}

/** Cached policy API URL set by {@link configureAmplify}. */
let signingApiUrl = '';

/** Cached tenant AWS account ID set by {@link configureAmplify}. */
let signingAwsAccount = '';

/** Cached tenant ID set by {@link configureAmplify}. */
let tenantId = '';

/** Cached Cognito user pool ID set by {@link configureAmplify}. (#2297) */
let userPoolId = '';

/** Cached AWS region set by {@link configureAmplify}. (#2297) */
let region = '';

/**
 * Cached "is SSO available for this tenant" flag set by {@link configureAmplify}.
 * Mirrors `tenantConfiguration.samlMetadata !== undefined` so the LoginPage can
 * render the "Sign in with single sign-on" button without re-fetching the
 * tenant configuration. (#2291)
 */
let ssoEnabled = false;

/**
 * Cached "SSO-only enforcement" flag set by {@link configureAmplify}.
 * Mirrors `tenantConfiguration.ssoOnly === true` so the LoginPage can decide
 * whether to auto-redirect to the IdP on mount (#2298). When true together
 * with {@link ssoEnabled}, the password/magic-link form is hidden entirely.
 */
let ssoOnly = false;

/**
 * Cached "is this the self-serve signer-demo tenant" flag set by
 * {@link configureAmplify}. Mirrors the server-computed
 * `tenantConfiguration.isDemo` (#2765) so the signer SPA can confine the demo
 * experience (no Signer Portal queue) and swap the post-sign terminal CTA to
 * the marketing signup — without trusting any client-only state. Real tenants
 * always see `false`.
 */
let isDemo = false;

/**
 * Production marketing-site base used when the runtime hostname doesn't match a
 * known pattern (localhost, preview hosts, SSR). Mirrors `PROD_HELP_BASE` in
 * {@link ./lib/helpUrl}.
 */
const PROD_BRANDING_BASE = 'https://www.yesaccedo.com';

/**
 * Returns the base URL for unauthenticated signing requests.
 * On non-localhost, returns `${origin}/api/policy` so requests route through CloudFront.
 * On localhost/127.0.0.1, returns the VITE_API_URL env variable (direct API Gateway URL for local dev).
 * Returns an empty string until {@link configureAmplify} has been called.
 */
export function getSigningApiUrl(): string {
    return signingApiUrl;
}

/**
 * Returns the tenant's AWS account ID used to set the X-Aws-Account header on signing requests.
 * Returns an empty string until {@link configureAmplify} has been called.
 */
export function getSigningAwsAccount(): string {
    return signingAwsAccount;
}

/**
 * Returns the tenant ID loaded from the tenant configuration.
 * Returns an empty string until {@link configureAmplify} has been called.
 */
export function getTenantId(): string {
    return tenantId;
}

/**
 * Returns the Cognito user pool ID loaded from the tenant configuration. Used
 * by Settings → SSO to surface the SAML Entity ID
 * (`urn:amazon:cognito:sp:<userPoolId>`) admins paste into IdPs that require
 * service-provider details up front (notably Google Workspace). (#2297)
 *
 * Returns an empty string until {@link configureAmplify} has been called.
 */
export function getUserPoolId(): string {
    return userPoolId;
}

/**
 * Returns the AWS region for this tenant's Cognito user pool. Used together
 * with {@link getTenantId} and {@link getSigningAwsAccount} by
 * {@link getCognitoHostedDomain} to derive the SAML ACS URL surfaced in
 * Settings → SSO. (#2297)
 *
 * Returns an empty string until {@link configureAmplify} has been called.
 */
export function getRegion(): string {
    return region;
}

/**
 * Returns whether SAML SSO is configured for this tenant. The LoginPage uses
 * this to decide whether to render the "Sign in with single sign-on" button.
 *
 * Mirrors `tenantConfiguration.samlMetadata !== undefined` from the
 * `/tenant-configuration` response — no extra fetch.
 *
 * Returns `false` until {@link configureAmplify} has been called. (#2291)
 */
export function getSsoEnabled(): boolean {
    return ssoEnabled;
}

/**
 * Returns whether the admin has enforced SSO-only login for this tenant.
 *
 * When `true` together with {@link getSsoEnabled}, the LoginPage auto-redirects
 * to the configured SAML IdP on mount and hides the password/magic-link form
 * entirely. When `false`, the IdP remains available via the "Sign in with
 * single sign-on" button alongside the password form.
 *
 * Mirrors `tenantConfiguration.ssoOnly === true` from the
 * `/tenant-configuration` response — no extra fetch.
 *
 * Returns `false` until {@link configureAmplify} has been called. (#2298)
 */
export function getSsoOnly(): boolean {
    return ssoOnly;
}

/**
 * Switches the Cognito token provider's key-value storage at sign-in time so
 * the "Stay signed in on this device" checkbox controls how long the session
 * survives.
 *
 * - `rememberMe = true` → `defaultStorage` (localStorage). Tokens survive
 *   browser restarts; combined with the user pool's 30-day refresh-token TTL,
 *   the user stays signed in for up to 30 days.
 * - `rememberMe = false` → `sessionStorage`. Tokens are dropped when the
 *   browser tab/window closes, so the user must re-authenticate on next visit.
 *
 * Must be called BEFORE {@link aws-amplify/auth.signIn} (or the magic-link
 * fetch that triggers a token issuance flow) so the storage swap is in effect
 * when Amplify persists the resulting tokens. (#1944)
 */
export function setLoginStorage(rememberMe: boolean): void {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(rememberMe ? defaultStorage : sessionStorage);
}

/**
 * Returns whether the current tenant is the self-serve signer-demo tenant
 * (#2765). Server-computed: mirrors `tenantConfiguration.isDemo` from the
 * `/tenant-configuration` response (no client-only state). When `true`, the
 * signer SPA confines the experience to the sample-policy signing flow and
 * swaps the post-sign terminal CTA to the marketing signup.
 *
 * Returns `false` until {@link configureAmplify} has been called.
 */
export function getIsDemo(): boolean {
    return isDemo;
}

/**
 * Resolves the marketing-site base URL for the current runtime, used to build
 * the demo signup call-to-action (#2765). Mirrors the host-derivation in
 * {@link ./lib/helpUrl} (the help center is hosted on the same branding site):
 *
 *   1. `*.yesaccedo.com` (or bare `yesaccedo.com`) → the prod marketing site.
 *   2. `*.dev.wadsworth.dev` → the per-TM dev branding deploy; strips the
 *      tenant + sub-account segments, keeping the last 4 segments, so
 *      `acme.546385742337.436484810012.dev.wadsworth.dev` maps to
 *      `https://436484810012.dev.wadsworth.dev`.
 *   3. Final fallback (localhost, SSR, preview hosts) → the prod marketing site.
 *
 * Derived at call time so tests that swap `window.location` behave correctly.
 */
export function getBrandingBaseUrl(): string {
    if (typeof window === 'undefined') {
        return PROD_BRANDING_BASE;
    }

    const { hostname, protocol } = window.location;

    if (hostname === 'yesaccedo.com' || hostname.endsWith('.yesaccedo.com')) {
        return `${protocol}//www.yesaccedo.com`;
    }

    if (hostname.endsWith('.dev.wadsworth.dev')) {
        const parts = hostname.split('.');
        if (parts.length >= 4) {
            return `${protocol}//${parts.slice(-4).join('.')}`;
        }
    }

    return PROD_BRANDING_BASE;
}

/**
 * Configures Amplify with tenant-specific settings fetched from the server.
 * Sets up Auth with the tenant's Cognito user pool, configures REST API endpoints,
 * and attaches a global Authorization header to all API calls.
 */
export async function configureAmplify() {
    const request = await fetch(`${import.meta.env.VITE_BASE_URL ?? ''}/tenant-configuration`);

    if (request.status === 404) {
        throw new TenantNotFoundError();
    }
    if (!request.ok) {
        throw new Error('Failed to load tenant configuration');
    }

    const tenantConfiguration = (await request.json()) as TenantConfiguration;

    if (!tenantConfiguration) {
        throw new Error('Tenant configuration not found');
    }

    // Use CloudFront path-based routing so the api-routes CloudFront Function
    // can extract the service name and route to the correct sub-account API Gateway.
    // Format: /api/{service}/... → routes to {service}.api.{awsAccount}.{base-domain}
    const origin = window.location.origin;

    Amplify.configure(
        {
            Auth: {
                Cognito: {
                    userPoolId: tenantConfiguration.userPoolId,
                    userPoolClientId: tenantConfiguration.userPoolClientId,
                    // OAuth / hosted-UI config for SAML SSO (#2291). Registered
                    // unconditionally because it is a no-op for tenants without
                    // a SAML IdP — nothing calls `signInWithRedirect` in that
                    // case. The `domain` MUST match the prefix CDK provisions
                    // at `tenant-resources-stack.ts` (drift-defended by
                    // `src/__tests__/cognito-domain.test.ts`).
                    //
                    // `redirectSignIn`/`redirectSignOut` use the bare
                    // `window.location.origin` WITHOUT a trailing slash to
                    // match the CDK `callbackUrls`/`logoutUrls` entry
                    // (`https://${subDomain}.${environment.domainName}` — no
                    // trailing slash). A mismatch would cause Cognito
                    // `redirect_mismatch` errors with no helpful hint.
                    loginWith: {
                        oauth: {
                            domain: getCognitoHostedDomain({
                                tenantId: tenantConfiguration.tenantId,
                                awsAccount: tenantConfiguration.awsAccount,
                                region: tenantConfiguration.region,
                            }),
                            scopes: ['email', 'openid', 'profile'],
                            redirectSignIn: [origin],
                            redirectSignOut: [origin],
                            responseType: 'code',
                        },
                    },
                },
            },
            API: {
                REST: {
                    PolicyTracker: {
                        endpoint: `${origin}/api/policy`,
                        region: tenantConfiguration.region ?? import.meta.env.VITE_AWS_REGION,
                    },
                    UserService: {
                        endpoint: `${origin}/api/user`,
                        region: tenantConfiguration.region ?? import.meta.env.VITE_AWS_REGION,
                    },
                    AuditService: {
                        endpoint: `${origin}/api/audit`,
                        region: tenantConfiguration.region ?? import.meta.env.VITE_AWS_REGION,
                    },
                    SupportService: {
                        endpoint: `${origin}/api/support`,
                        region: tenantConfiguration.region ?? import.meta.env.VITE_AWS_REGION,
                    },
                },
            },
        },
        {
            API: {
                REST: {
                    /**
                     * Global headers function that attaches the Cognito ID token as an
                     * Authorization Bearer header to every REST API request.
                     * Returns an empty object if no session is available (e.g., signed out).
                     */
                    headers: async (): Promise<Record<string, string>> => {
                        try {
                            const session = await fetchAuthSession();
                            const token = session.tokens?.idToken?.toString();
                            if (token) {
                                return { Authorization: `Bearer ${token}` };
                            }
                            return {};
                        } catch {
                            return {};
                        }
                    },
                },
            },
        }
    );

    // Point Amplify at whichever storage backend currently holds the user's
    // Cognito tokens. This must run AFTER Amplify.configure(...): in v6,
    // first-time configure unconditionally calls setKeyValueStorage(defaultStorage)
    // during init, so the probe MUST override that with the right backend
    // after configure returns. (Regression #1985 / re-regression #2035.)
    cognitoUserPoolsTokenProvider.setKeyValueStorage(pickTokenStorage());

    // Warm up the token provider by forcing it to load tokens from storage.
    // This ensures that subsequent fetchAuthSession calls (e.g., from the
    // REST headers function) return cached tokens immediately, avoiding a
    // race condition where API calls fire before tokens are available.
    try {
        await fetchAuthSession();
    } catch {
        // Swallow errors — user may not be authenticated yet (fresh visit).
    }

    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        signingApiUrl = import.meta.env.VITE_API_URL || '';
    } else {
        signingApiUrl = `${origin}/api/policy`;
    }

    signingAwsAccount = tenantConfiguration.awsAccount;
    tenantId = tenantConfiguration.tenantId;
    userPoolId = tenantConfiguration.userPoolId;
    region = tenantConfiguration.region;
    ssoEnabled = tenantConfiguration.samlMetadata !== undefined;
    ssoOnly = tenantConfiguration.ssoOnly === true;
    isDemo = tenantConfiguration.isDemo === true;
}
